import type { JobAction, JobReasonInput } from '@chukua/common';
import { Button, Tag } from 'antd';
import { useState } from 'react';
import { JobActionReasonModal } from './JobActionReasonModal';
import { JobQuoteValidUntilModal } from './JobQuoteValidUntilModal';
import { JOB_STATUS_COLORS, JOB_STATUS_LABELS } from '../policies/job-status-labels';
import type { JobDetail } from '../jobs.api';
import type { useJobActions } from '../hooks/use-job-actions';
import type { JobReasonCodeAction } from '../hooks/use-job-reason-code-options';

const ACTION_LABELS: Record<JobAction, string> = {
  quote: 'Quote',
  confirm: 'Confirm',
  cancel: 'Cancel',
  hold: 'Hold',
  resume: 'Resume',
  close: 'Close',
};

/** `hold`/`cancel` always need a reason (A2) — every other action fires straight away. */
function reasonRequiredAction(action: JobAction): JobReasonCodeAction | null {
  return action === 'hold' || action === 'cancel' ? action : null;
}

interface JobStateActionsProps {
  job: JobDetail;
  /** Hidden entirely, not just disabled, without `job.create` — nav-hiding is UX only, the server remains the authority. */
  canManage: boolean;
  actions: ReturnType<typeof useJobActions>;
}

/** One button per `job.availableActions`, in the detail page's header (visible on every tab, since state isn't tab-scoped). */
export function JobStateActions({ job, canManage, actions }: JobStateActionsProps) {
  const [reasonAction, setReasonAction] = useState<JobReasonCodeAction | null>(null);
  const [quoting, setQuoting] = useState(false);
  const statusTag = (
    <Tag color={JOB_STATUS_COLORS[job.status as keyof typeof JOB_STATUS_COLORS]}>{JOB_STATUS_LABELS[job.status as keyof typeof JOB_STATUS_LABELS]}</Tag>
  );

  if (!canManage) return statusTag;

  const noReasonActions: Partial<Record<JobAction, (job: JobDetail) => Promise<void>>> = {
    confirm: actions.confirm,
    resume: actions.resume,
    close: actions.close,
  };

  function handleClick(action: JobAction) {
    if (action === 'quote') {
      setQuoting(true);
      return;
    }
    const withReason = reasonRequiredAction(action);
    if (withReason) {
      setReasonAction(withReason);
      return;
    }
    void noReasonActions[action]?.(job);
  }

  async function submitReason(input: JobReasonInput) {
    if (reasonAction === 'hold') await actions.hold(job, input);
    else if (reasonAction === 'cancel') await actions.cancel(job, input);
    setReasonAction(null);
  }

  async function submitQuote(validUntil: string) {
    await actions.quote(job, validUntil);
    setQuoting(false);
  }

  return (
    <>
      <div className="flex items-center gap-2">
        {statusTag}
        {job.availableActions.map((action) => (
          <Button key={action} size="small" onClick={() => handleClick(action)} loading={actions.pendingAction === action}>
            {ACTION_LABELS[action]}
          </Button>
        ))}
      </div>
      <JobActionReasonModal
        action={reasonAction}
        title={reasonAction ? `${ACTION_LABELS[reasonAction]} job ${job.number}` : ''}
        onCancel={() => setReasonAction(null)}
        onSubmit={submitReason}
      />
      <JobQuoteValidUntilModal
        open={quoting}
        jobNumber={job.number}
        submitting={actions.pendingAction === 'quote'}
        onCancel={() => setQuoting(false)}
        onSubmit={submitQuote}
      />
    </>
  );
}
